import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Star, ChevronLeft, ChevronRight, Quote } from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';

const REVIEWS = [
  {
    id: 1,
    name: "Marco B.",
    country_it: "Italia",
    country_en: "Italy",
    photo: "https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=150&h=150&fit=crop&crop=face",
    rating: 5,
    guide: "iceland",
    text_it: "La guida dell'Islanda è stata fondamentale per il nostro viaggio. I consigli insider ci hanno permesso di evitare le folle turistiche e scoprire luoghi magici.",
    text_en: "The Iceland guide was essential for our trip. The insider tips helped us avoid tourist crowds and discover magical places."
  },
  {
    id: 2,
    name: "Sarah K.",
    country_it: "Germania",
    country_en: "Germany",
    photo: "https://images.unsplash.com/photo-1494790108377-be9c29b29330?w=150&h=150&fit=crop&crop=face",
    rating: 5,
    guide: "japan",
    text_it: "Dopo aver usato questa guida per il Giappone, non tornerò mai più alle guide tradizionali. Le informazioni pratiche sono state preziosissime.",
    text_en: "After using this guide for Japan, I'll never go back to traditional guides. The practical info was invaluable."
  },
  {
    id: 3,
    name: "Alessandro M.",
    country_it: "Svizzera",
    country_en: "Switzerland",
    photo: "https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?w=150&h=150&fit=crop&crop=face",
    rating: 5,
    guide: "sicily",
    text_it: "Da siciliano emigrato, ho regalato questa guida a tutti i miei amici stranieri. Finalmente qualcuno che capisce davvero la Sicilia!",
    text_en: "As an emigrated Sicilian, I've gifted this guide to all my foreign friends. Finally someone who truly understands Sicily!"
  },
  {
    id: 4,
    name: "Emma L.",
    country_it: "Regno Unito",
    country_en: "United Kingdom",
    photo: "https://images.unsplash.com/photo-1438761681033-6461ffad8d80?w=150&h=150&fit=crop&crop=face",
    rating: 5,
    guide: "new_zealand",
    text_it: "Ho passato 3 settimane in Nuova Zelanda seguendo questa guida. Ogni giorno era perfettamente pianificato. Esperienza indimenticabile!",
    text_en: "I spent 3 weeks in New Zealand following this guide. Every day was perfectly planned. Unforgettable experience!"
  },
  {
    id: 5,
    name: "Giovanni P.",
    country_it: "Italia",
    country_en: "Italy",
    photo: "https://images.unsplash.com/photo-1519085360753-af0119f7cbe7?w=150&h=150&fit=crop&crop=face",
    rating: 5,
    guide: "japan",
    text_it: "Fotografo professionista. Gli spot foto suggeriti nella guida del Giappone sono incredibili. Ho scattato le migliori foto della mia carriera.",
    text_en: "Professional photographer here. The photo spots in the Japan guide are incredible. I took the best photos of my career."
  }
];

const Reviews = () => {
  const { language } = useLanguage();
  const [currentIndex, setCurrentIndex] = useState(0);
  
  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentIndex((prev) => (prev + 1) % REVIEWS.length);
    }, 6000);
    return () => clearInterval(interval);
  }, []);

  const texts = {
    it: {
      title: "Cosa dicono i viaggiatori",
      subtitle: "Recensioni verificate da chi ha già usato le nostre guide"
    },
    en: {
      title: "What travelers say",
      subtitle: "Verified reviews from those who have used our guides"
    }
  };

  const t = texts[language];
  const review = REVIEWS[currentIndex];
  const reviewText = language === 'it' ? review.text_it : review.text_en;
  const reviewCountry = language === 'it' ? review.country_it : review.country_en;

  return (
    <section className="py-24 bg-[#111]" data-testid="reviews-section">
      <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-20">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="text-center mb-16"
        >
          <div className="flex items-center justify-center gap-1 mb-4">
            {[...Array(5)].map((_, i) => (
              <Star key={i} className="w-5 h-5 text-[#C9B896] fill-[#C9B896]" />
            ))}
            <span className="text-white font-semibold ml-2">4.9</span>
          </div>
          <h2 className="text-4xl md:text-5xl font-bold text-white mb-4">
            {t.title}
          </h2>
          <p className="text-lg text-[#888]">
            {t.subtitle}
          </p>
        </motion.div>

        {/* Review Card */}
        <div className="relative max-w-3xl mx-auto">
          <button
            onClick={() => setCurrentIndex((prev) => (prev - 1 + REVIEWS.length) % REVIEWS.length)}
            className="absolute left-0 top-1/2 -translate-y-1/2 -translate-x-4 lg:-translate-x-16 w-12 h-12 bg-[#1a1a1a] hover:bg-[#222] rounded-full flex items-center justify-center transition-colors z-10"
          >
            <ChevronLeft className="w-5 h-5 text-white" />
          </button>

          <button
            onClick={() => setCurrentIndex((prev) => (prev + 1) % REVIEWS.length)}
            className="absolute right-0 top-1/2 -translate-y-1/2 translate-x-4 lg:translate-x-16 w-12 h-12 bg-[#1a1a1a] hover:bg-[#222] rounded-full flex items-center justify-center transition-colors z-10"
          >
            <ChevronRight className="w-5 h-5 text-white" />
          </button>

          <AnimatePresence mode="wait">
            <motion.div
              key={currentIndex}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              transition={{ duration: 0.4 }}
              className="bg-[#1a1a1a] rounded-2xl p-8 md:p-12 text-center"
            >
              <Quote className="w-10 h-10 text-[#C9B896]/30 mx-auto mb-6" />
              
              <p className="text-xl md:text-2xl text-white leading-relaxed mb-8">
                "{reviewText}"
              </p>

              <div className="flex items-center justify-center gap-4">
                <img
                  src={review.photo}
                  alt={review.name}
                  className="w-12 h-12 rounded-full object-cover"
                />
                <div className="text-left">
                  <p className="text-white font-medium">{review.name}</p>
                  <p className="text-[#888] text-sm">{reviewCountry}</p>
                </div>
              </div>
            </motion.div>
          </AnimatePresence>

          {/* Dots */}
          <div className="flex justify-center gap-2 mt-8">
            {REVIEWS.map((_, i) => (
              <button
                key={i}
                onClick={() => setCurrentIndex(i)}
                className={`h-2 rounded-full transition-all duration-300 ${
                  i === currentIndex ? 'bg-[#C9B896] w-8' : 'bg-[#333] w-2'
                }`}
              />
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

export default Reviews;
